import {
    formatCommentDate,
    formatCommentDateTime,
    getCommentSortMs,
} from '../../../utils/dates/dates.utils';

const ADI_PROFESSOR_EMAIL = 'adi@aden.org';

const hasText = (value) => typeof value === 'string' && value.trim().length > 0;

export const isAdiProfessorEmail = (email) =>
    typeof email === 'string' &&
    email.trim().toLowerCase() === ADI_PROFESSOR_EMAIL;

const parseSubmissionIdForMerge = (value) => {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
};

const normalizeCommentBody = (text) => {
    if (typeof text !== 'string') return '';
    return text.replace(/\s+/g, ' ').trim();
};

const formatDisplayDate = (value) =>
    `${formatCommentDate(value)} ${formatCommentDateTime(value)}`;

const buildLegacyComment = ({
    id,
    name,
    comment,
    date,
    photo,
    sender,
    authorIsTeacher,
    commentType,
    legacyKind,
    sourceSubmissionId,
}) => ({
    id,
    name,
    comment,
    date: formatDisplayDate(date),
    sort_ms: getCommentSortMs(date),
    photo: photo || null,
    sender,
    author_is_teacher: authorIsTeacher,
    comment_type: commentType,
    image: null,
    attachments: {
        fileUrl: null,
        audioUrl: null,
        videoUrl: null,
    },
    is_legacy: true,
    legacy_kind: legacyKind,
    legacy_source_submission_id: sourceSubmissionId || null,
});

export const buildLegacyCommentsFromSubmission = ({
    submission,
    student,
    professor,
} = {}) => {
    if (!submission || typeof submission !== 'object') return [];

    const sourceSubmissionId =
        submission.id || submission.submission_id || submission.order || 'na';
    const studentName =
        submission.student?.name ||
        student?.name ||
        submission.student_name ||
        'Estudiante';
    const studentPhoto =
        submission.student?.image_url ||
        student?.image_url ||
        submission.student_picture ||
        null;
    const professorName =
        submission.professor?.name ||
        professor?.name ||
        submission.professor_name ||
        submission.teacher_name ||
        'Profesor tutor';
    const professorPhoto =
        submission.professor?.image_url ||
        professor?.image_url ||
        submission.professor_image ||
        submission.teacher_picture ||
        null;
    const legacyComments = [];

    if (hasText(submission.student_comment)) {
        legacyComments.push(
            buildLegacyComment({
                id: `legacy-submission-${sourceSubmissionId}`,
                name: studentName,
                comment: submission.student_comment.trim(),
                date: submission.create_date,
                photo: studentPhoto,
                sender: 'other',
                authorIsTeacher: false,
                commentType: 'submission',
                legacyKind: 'submission',
                sourceSubmissionId,
            })
        );
    }

    if (hasText(submission.professor_comment)) {
        legacyComments.push(
            buildLegacyComment({
                id: `legacy-correction-${sourceSubmissionId}`,
                name: professorName,
                comment: submission.professor_comment.trim(),
                date: submission.date_of_correction,
                photo: professorPhoto,
                sender: 'user',
                authorIsTeacher: true,
                commentType: 'correction',
                legacyKind: 'correction',
                sourceSubmissionId,
            })
        );
    }

    return legacyComments;
};

export const mergeLegacyAndConversationComments = (
    legacyComments = [],
    conversationComments = [],
    options = {}
) => {
    const normalizedLegacy = Array.isArray(legacyComments)
        ? legacyComments.filter(Boolean)
        : [];
    const legacyIds = new Set(normalizedLegacy.map((item) => item.id));
    const normalizedConversation = Array.isArray(conversationComments)
        ? conversationComments.filter((item) => item && !legacyIds.has(item.id))
        : [];

    const submissionId = parseSubmissionIdForMerge(options.submissionId);
    const isAdiProfessor = options.isAdiProfessor === true;

    let filteredLegacy = normalizedLegacy;

    if (normalizedConversation.length > 0) {
        const matchesConversation = (legacyItem, kind) => {
            const target = normalizeCommentBody(legacyItem.comment);
            return normalizedConversation.some((m) => {
                if (m.comment_type !== kind) return false;
                const sameSubmission =
                    submissionId != null &&
                    parseSubmissionIdForMerge(m.submission_id) === submissionId;
                const sameText =
                    target.length > 0 &&
                    normalizeCommentBody(m.comment) === target;
                return sameSubmission || sameText;
            });
        };

        filteredLegacy = normalizedLegacy.filter((leg) => {
            if (leg.legacy_kind === 'submission') {
                return !matchesConversation(leg, 'submission');
            }
            if (leg.legacy_kind === 'correction') {
                if (isAdiProfessor) return true;
                return !matchesConversation(leg, 'correction');
            }
            return true;
        });
    }

    const combined = [...filteredLegacy, ...normalizedConversation].sort(
        (a, b) => {
            const da = a.sort_ms ?? Number.MAX_SAFE_INTEGER;
            const db = b.sort_ms ?? Number.MAX_SAFE_INTEGER;
            if (da !== db) return da - db;
            const ia = a.id;
            const ib = b.id;
            if (ia === ib) return 0;
            if (typeof ia === 'number' && typeof ib === 'number')
                return ia - ib;
            return String(ia).localeCompare(String(ib));
        }
    );

    return combined;
};
